import React, { useState, useEffect } from 'react';
import { Line, HorizontalBar, Bar, Doughnut, Pie, Radar, Polar, } from 'react-chartjs-2';
import {
  Page,
  withReload,
} from "components/common";
import { withXeroErrors } from "components";
import { actions } from "react-redux-form";
import {
  compose,
  withPropsOnChange,
  withStateHandlers,
  branch,
  withHandlers,
} from "recompose";
import { getMonthEnd, getMonthStart } from "../Xero/Reports/util";
import { formatDate } from "../../services/util";
import moment from "moment";
import BlockCard from "./BlockCard"
import MainCard from "./MainCard"
import { NotificationManager } from 'react-notifications';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import LoadingMsg from 'components/common/LoadingMsg';

const cardColors = ['#5cb85c', '#f0ad4e', '#d9534f'];


const Dash = (item) => {

  const { t } = useTranslation()
  const history = useHistory()

  const data = item.item;
  const balanceSheet = item.balanceSheet;
  const invoiceData = item.invoiceData;
  const bankdata = item.bankdata;

  const getRandomColors = (count) => {
    const colors = [];
    for (let i = 0; i < count; i++) {
      const color = `rgba(${Math.floor(Math.random() * 256)}, ${Math.floor(Math.random() * 256)}, ${Math.floor(
        Math.random() * 256
      )}, 0.6)`;
      colors.push(color);
    }
    return colors;
  };

  {/* Receivables and Payables Functions */ }

  function processAccountData(data) {
    const length = data ? data.length : 0;
    const account = data ? data : [];
    let lengthInvoices = 0;
    let lengthBills = 0;
    let sumAmountPaidInvoices = 0;
    let sumAmountDueInvoices = 0;
    let sumAmountPaidBills = 0;
    let sumAmountDueBills = 0;

    if (length > 0) {
      for (let i = 0; i < length; i++) {

        if (account[i].Type === "ACCREC") {
          lengthInvoices++;
          sumAmountDueInvoices += account[i].AmountDue;
          sumAmountPaidInvoices += account[i].Total;
        } else if (account[i].Type === "ACCPAY") {
          lengthBills++;
          sumAmountDueBills += account[i].AmountDue;
          sumAmountPaidBills += account[i].Total;
        }
      }
    } else {
      NotificationManager.warning("No invoice data available");
    }

    return {
      lengthInvoices,
      lengthBills,
      sumAmountPaidInvoices,
      sumAmountDueInvoices,
      sumAmountPaidBills,
      sumAmountDueBills,
    };
  }

  const {
    lengthInvoices,
    lengthBills,
    sumAmountPaidInvoices,
    sumAmountDueInvoices,
    sumAmountPaidBills,
    sumAmountDueBills,
  } = processAccountData(invoiceData);

  {/* Bank Accounts and Cash Flow Functions */ }

  const sectionBanks = bankdata.Rows.find(row => row.RowType === 'Section');

  const getSummaryCellValue = (bankdata, k, n) => {
    if (sectionBanks && sectionBanks.Rows.length > 0) {
      const summaryRow = sectionBanks.Rows[k];

      if (summaryRow && summaryRow.Cells) {
        return summaryRow.Cells[n]?.Value;
      }
    }

    return undefined;
  };

  const shuffleArray = (array) => {
    const shuffledArray = [...array];
    for (let i = shuffledArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledArray[i], shuffledArray[j]] = [shuffledArray[j], shuffledArray[i]];
    }
    return shuffledArray;
  };

  const getRandomIndexes = () => {
    const numIndexes = Math.min(8, sectionBanks.Rows.length);
    const allIndexes = Array.from({ length: sectionBanks.Rows.length }, (_, index) => index);
    const shuffledIndexes = shuffleArray(allIndexes.slice(0, -1));
    return shuffledIndexes.slice(0, numIndexes);
  };

  const randomIndexes = getRandomIndexes();
  const foundNums = Array.from({ length: 8 }, (_, i) => i + 1);

  const accountDetails = foundNums.map((num) => {
    const accountName = getSummaryCellValue(bankdata, randomIndexes[num - 1], 0);
    const cashReceived = getSummaryCellValue(bankdata, randomIndexes[num - 1], 2);
    const cashSpent = getSummaryCellValue(bankdata, randomIndexes[num - 1], 3);
    const closingBalance = getSummaryCellValue(bankdata, randomIndexes[num - 1], 4);

    return {
      accountName: accountName || undefined,
      cashReceived: cashReceived || undefined,
      cashSpent: cashSpent || undefined,
      closingBalance: closingBalance || undefined,
    };
  });

  const { 0: one, 1: two, 2: three, 3: four, 4: five, 5: six, 6: seven, 7: eight } = accountDetails;

  const [bankBalanceChartData, setBankBalanceChartData] = useState({});

  const cashDues = [
    one, two, three, four, five, six, seven, eight
  ]
    .map((item) => {
      if (item && item.cashReceived !== undefined && item.cashSpent !== undefined) {
        return parseFloat(item.cashReceived - item.cashSpent).toFixed(2);
      }
      return undefined;
    })
    .filter((item) => item !== undefined);


  {/* Profit and Loss Functions */ }

  const sectionPandL = data.Rows.filter(row => row.RowType === 'Section');

  const findRowValue = (sections, targetValueName) => {
    for (const sectionPandL of sections) {
      for (const row of sectionPandL.Rows) {
        if (row.RowType === 'Row') {
          const valueName = row.Cells[0].Value;
          const values = row.Cells.slice(1).map(cell => cell.Value);

          if (valueName === targetValueName) {
            return { valueName, values };
          }
        }
      }
    }
    return { valueName: '', values: Array(12).fill('') };
  };

  const fetchData = (sections, targetValueName) => {
    const { valueName, values } = findRowValue(sections, targetValueName);
    return [valueName, ...values];
  };

  const sectionsWithEmptyTitle = data.Rows.filter(row => row.RowType === 'Section' && row.Title === '');

  const [
    grossProfitName,
    grossProfitValue1,
    grossProfitValue2,
    grossProfitValue3,
    grossProfitValue4,
    grossProfitValue5,
    grossProfitValue6,
    grossProfitValue7,
    grossProfitValue8,
    grossProfitValue9,
    grossProfitValue10,
    grossProfitValue11,
    grossProfitValue12,
  ] = fetchData(sectionsWithEmptyTitle, 'Gross Profit');

  const [
    netProfitName,
    netProfitValue1,
    netProfitValue2,
    netProfitValue3,
    netProfitValue4,
    netProfitValue5,
    netProfitValue6,
    netProfitValue7,
    netProfitValue8,
    netProfitValue9,
    netProfitValue10,
    netProfitValue11,
    netProfitValue12,
  ] = fetchData(sectionsWithEmptyTitle, 'Net Profit');


  const getSummaryHeader = (data, n) => {
    const sectionPandL = data.Rows.find(row => row.RowType === 'Header');
    return sectionPandL?.Cells[n]?.Value;
  };

  const headers = Array.from({ length: 13 }, (_, index) => getSummaryHeader(data, index));

  const extractWords = (str) => {
    if (str && typeof str === 'string') {
      return str.substring(3);
    }
    return '';
  };

  const getProfitCells = (data, sectionTitle, n) => {
    const sectionPandL = data.Rows.find(row => row.RowType === 'Section' && row.Title === sectionTitle);
    const summaryRow = sectionPandL?.Rows.find(row => row.RowType === 'SummaryRow');

    return summaryRow ? summaryRow.Cells[n]?.Value : undefined;
  };

  const income = Array.from({ length: 13 }, (_, index) => getProfitCells(data, "Income", index));
  const expensesLess = Array.from({ length: 13 }, (_, index) => getProfitCells(data, "Less Operating Expenses", index));

  const [incomeName,
    incomeNumber1,
    incomeNumber2,
    incomeNumber3,
    incomeNumber4,
    incomeNumber5,
    incomeNumber6,
    incomeNumber7,
    incomeNumber8,
    incomeNumber9,
    incomeNumber10,
    incomeNumber11,
    incomeNumber12] = income;

  const [expensesLessName,
    expensesLessValue1,
    expensesLessValue2,
    expensesLessValue3,
    expensesLessValue4,
    expensesLessValue5,
    expensesLessValue6,
    expensesLessValue7,
    expensesLessValue8,
    expensesLessValue9,
    expensesLessValue10,
    expensesLessValue11,
    expensesLessValue12,] = expensesLess;

  const [profitLossChartData, setProfitLossChartData] = useState({});

  {/* Balance Sheet Functions */ }

  const sectionBalance = balanceSheet.Rows.filter(row => row.RowType === 'Section');
  const sectionLength = sectionBalance.length;

  const summaryRowBalance = sectionBalance.map(sectionItem => sectionItem.Rows.find(row => row.RowType === 'SummaryRow')).filter(row => row !== undefined);

  const getValueFromSummaryRow = (summaryRowBalance, targetValue) => {
    const targetRow = summaryRowBalance.find(
      (row) => row !== undefined && row.Cells[0]?.Value === targetValue
    );

    const tName = targetRow ? targetRow.Cells[0]?.Value : undefined;
    const tValue = targetRow ? targetRow.Cells[1]?.Value : undefined;

    return { tName, tValue };
  };

  const totalAssetsValues = getValueFromSummaryRow(summaryRowBalance, 'Total Assets');
  const totalLiabilitiesValues = getValueFromSummaryRow(summaryRowBalance, 'Total Liabilities');
  const totalEquityValues = getValueFromSummaryRow(summaryRowBalance, 'Total Equity');

  {/* Expenses Functions */ }

  const sectionTitleExpense = "Less Operating Expenses";

  const getTotalExpenses = (data, n) => {
    const sectionExpenses = data.Rows.find(row => row.RowType === 'Section' && row.Title === sectionTitleExpense);
    const summaryRow = sectionExpenses?.Rows.find(row => row.RowType === 'SummaryRow');

    return summaryRow ? summaryRow.Cells[n]?.Value : undefined;
  };

  const totalExpensesName = getTotalExpenses(data, 0);

  const totalExpensesValues = Array.from({ length: 12 }, (_, index) => {
    return parseInt(getTotalExpenses(data, index + 1)) || 0;
  });

  const totalExpensesSum = totalExpensesValues.reduce((acc, value) => acc + value, 0);

  const sectionExpenses = data.Rows.find(
    (row) => row.RowType === 'Section' && row.Title === sectionTitleExpense
  );

  const numberOfRows = sectionExpenses?.Rows?.length || 0;

  const firstRow = sectionExpenses?.Rows?.[0];
  const numberOfValueCells = firstRow?.Cells?.length || 0;

  const getExpensesCell = (data, n, k) => {
    const summaryRows = sectionExpenses?.Rows?.filter((row) => row.RowType === 'Row');
    const summaryRow = summaryRows ? summaryRows[n] : undefined;

    return summaryRow && summaryRow.Cells && summaryRow.Cells[k] ? summaryRow.Cells[k].Value : undefined;
  };

  const rowTotalSums = Array.from({ length: numberOfRows }, (_, index) => {
    const rowName = getExpensesCell(data, index, 0);
    const totalSum = Array.from({ length: numberOfValueCells }, (_, cellIndex) => {
      const cellValue = getExpensesCell(data, index, cellIndex + 1);
      return parseFloat(cellValue) || 0;
    }).reduce((acc, value) => acc + value, 0);

    return { rowIndex: index, rowName, totalSum };
  });

  const sortedTotalSums = rowTotalSums.sort((a, b) => b.totalSum - a.totalSum);
  const top5TotalSums = sortedTotalSums.slice(0, 5);

  const totalPercentage = (value, total) => {
    if (total === 0) return 0;

    const percentage = (value / total) * 100;
    return Math.min(100, Math.max(0, parseFloat(percentage.toFixed(2))));
  };

  const [value1, value2, value3, value4, value5] = Array.from({ length: 5 }, (_, index) =>
    top5TotalSums.length > index
      ? totalPercentage(top5TotalSums[index]?.totalSum, totalExpensesSum) || 0
      : 0
  );

  function convertRowName(rowName) {
    return rowName?.replace(/[-0-9]/g, digit => String.fromCharCode(1632 + parseInt(digit))) || "Error";
  }

  const displayRowName1 = convertRowName(top5TotalSums[0]?.rowName);
  const displayRowName2 = convertRowName(top5TotalSums[1]?.rowName);
  const displayRowName3 = convertRowName(top5TotalSums[2]?.rowName);
  const displayRowName4 = convertRowName(top5TotalSums[3]?.rowName);
  const displayRowName5 = convertRowName(top5TotalSums[4]?.rowName);

  const otherExpenses = "Other Expenses"

  const adjustedValue = parseFloat(Math.max(0, 100 - value1 - value2 - value3 - value4 - value5)).toFixed(2);;

  {/* Extra Functions  */ }

 const cardStyle =  {
    className: 'my-4',
    style: { flex: '1 1 400px', minWidth: '0' }
  }

  {/* All Filters */ }


  const filterExpenses = (data) => {
    return data.filter((item) => !(item.expenseName === "Error" && item.value === 0));
  };

  const filterBanks = (data) => data.filter(item => item.balance != null);

  const filterCashFlow = (data) => data.filter(item => item.cashReceived && item.cashSpent != null);

  const filterBalance = (values) => values.filter(value => value !== undefined);

  const filterProfitLoss = (data) => data.filter(item => item.value != null);
  const generateFilterData = (headers, values, valueExtractor) => {
    return filterProfitLoss(values.map((value, index) => ({
      id: index + 1,
      month: extractWords(headers[12 - index]),
      value: valueExtractor(value),
    })));
  };



  {/* All Data Tables */ }

  const bankBalanceData = filterBanks([
    { id: 1, name: one.accountName, balance: one.closingBalance },
    { id: 2, name: two.accountName, balance: two.closingBalance },
    { id: 3, name: three.accountName, balance: three.closingBalance },
    { id: 4, name: four.accountName, balance: four.closingBalance },
    { id: 5, name: five.accountName, balance: five.closingBalance },
    { id: 6, name: six.accountName, balance: six.closingBalance },
    { id: 7, name: seven.accountName, balance: seven.closingBalance },
    { id: 8, name: eight.accountName, balance: eight.closingBalance },
  ]);

  const cashFlowData = filterCashFlow([
    { id: 1, name: one.accountName, cashSpent: one.cashSpent, cashReceived: one.cashReceived, cashDue: cashDues[0] },
    { id: 2, name: two.accountName, cashSpent: two.cashSpent, cashReceived: two.cashReceived, cashDue: cashDues[1] },
    { id: 3, name: three.accountName, cashSpent: three.cashSpent, cashReceived: three.cashReceived, cashDue: cashDues[2] },
    { id: 4, name: four.accountName, cashSpent: four.cashSpent, cashReceived: four.cashReceived, cashDue: cashDues[3] },
    { id: 5, name: five.accountName, cashSpent: five.cashSpent, cashReceived: five.cashReceived, cashDue: cashDues[4] },
    { id: 6, name: six.accountName, cashSpent: six.cashSpent, cashReceived: six.cashReceived, cashDue: cashDues[5] },
    { id: 7, name: seven.accountName, cashSpent: seven.cashSpent, cashReceived: seven.cashReceived, cashDue: cashDues[6] },
    { id: 8, name: eight.accountName, cashSpent: eight.cashSpent, cashReceived: eight.cashReceived, cashDue: cashDues[7] }
  ]);

  const expensesDataFive = filterExpenses([
    { id: 1, expenseName: displayRowName1, value: value1, color: 'rgba(255, 205, 86, 1)' },
    { id: 2, expenseName: displayRowName2, value: value2, color: 'rgba(255, 205, 86, 0.8)' },
    { id: 3, expenseName: displayRowName3, value: value3, color: 'rgba(255, 205, 86, 0.6)' },
    { id: 4, expenseName: displayRowName4, value: value4, color: 'rgba(255, 205, 86, 0.4)' },
    { id: 5, expenseName: displayRowName5, value: value5, color: 'rgba(255, 205, 86, 0.2)' },
    { id: 6, expenseName: otherExpenses, value: adjustedValue, color: 'rgba(255, 205, 86, 0.1)' },
  ]);

  const [expensesChartDataFour, setExpensesDataFour] = useState({});



  const revenueData = generateFilterData(
    headers,
    [incomeNumber12,
      incomeNumber11,
      incomeNumber10,
      incomeNumber9,
      incomeNumber8,
      incomeNumber7,
      incomeNumber6,
      incomeNumber5,
      incomeNumber4,
      incomeNumber3,
      incomeNumber2,
      incomeNumber1],
    (value) => value
  );

  const expensesData = generateFilterData(
    headers,
    [expensesLessValue12,
      expensesLessValue11,
      expensesLessValue10,
      expensesLessValue9,
      expensesLessValue8,
      expensesLessValue7,
      expensesLessValue6,
      expensesLessValue5,
      expensesLessValue4,
      expensesLessValue3,
      expensesLessValue2,
      expensesLessValue1],
    (value) => value
  );

  const grossProfit = generateFilterData(
    headers,
    [grossProfitValue12,
      grossProfitValue11,
      grossProfitValue10,
      grossProfitValue9,
      grossProfitValue8,
      grossProfitValue7,
      grossProfitValue6,
      grossProfitValue5,
      grossProfitValue4,
      grossProfitValue3,
      grossProfitValue2,
      grossProfitValue1],
    (value) => value
  );

  const netProfit = generateFilterData(
    headers,
    [netProfitValue12,
      netProfitValue11,
      netProfitValue10,
      netProfitValue9,
      netProfitValue8,
      netProfitValue7,
      netProfitValue6,
      netProfitValue5,
      netProfitValue4,
      netProfitValue3,
      netProfitValue2,
      netProfitValue1],
    (value) => value
  );



  const chartLabels = filterBalance([t(totalAssetsValues.tName), t(totalEquityValues.tName), t(totalLiabilitiesValues.tName)]);
  const chartDataBalance = filterBalance([totalAssetsValues.tValue, totalEquityValues.tValue, totalLiabilitiesValues.tValue]);



  {/* All Conversion Tables */ }

  const convertProfitLossTable = (tableData, labelKey, dataKey) => {
    const labels = tableData.map((item) => item[labelKey]);
    const data = tableData.map((item) => item[dataKey]);

    const datasets = [
      {
        label: 'Chart Data',
        data,
        backgroundColor: 'rgba(75,192,192,0.2)',
        borderColor: 'rgba(75,192,192,1)',
        borderWidth: 1,
      },
    ];

    return { labels, datasets };
  };


  useEffect(() => {
    const profitLossChart = convertProfitLossTable(revenueData, 'month', 'value', 'line');

    setProfitLossChartData(profitLossChart);


    const chartDataPandL = {
      labels: profitLossChart.labels,
      datasets: [
        {
          label: t('Revenue'),
          data: profitLossChart.datasets[0].data,
          borderColor: 'rgba(75, 192, 192, 1)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          borderWidth: 1,
          fill: false,
          lineTension: 0.1,
        },
        {
          label: t('Expenses'),
          data: convertProfitLossTable(expensesData, 'month', 'value', 'line').datasets[0].data,
          borderColor: 'rgba(255, 99, 132, 1)',
          backgroundColor: 'rgba(255, 99, 132, 0.2)',
          borderWidth: 1,
          fill: false,
          lineTension: 0.1,
        },
        {
          label: t('Gross Profit'),
          data: convertProfitLossTable(grossProfit, 'month', 'value', 'line').datasets[0].data,
          borderColor: 'rgba(255, 205, 86, 1)',
          backgroundColor: 'rgba(255, 205, 86, 0.2)',
          borderWidth: 1,
          fill: false,
          lineTension: 0.1,
        },
        {
          label: t('Net Profit'),
          data: convertProfitLossTable(netProfit, 'month', 'value', 'line').datasets[0].data,
          borderColor: 'rgba(54, 162, 235, 1)',
          backgroundColor: 'rgba(54, 162, 235, 0.2)',
          borderWidth: 1,
          fill: false,
          lineTension: 0.1,
        },
      ],
    };

    setProfitLossChartData(chartDataPandL);
  }, []);


  useEffect(() => {
    const bankBalanceChart = convertTableToChartData(bankBalanceData, 'name', 'balance', 'bar');
    setBankBalanceChartData(bankBalanceChart);

  }, []);

  const convertTableToChartData = (tableData, labelKey, dataKey, chartType) => {
    const labels = tableData.map(item => item[labelKey]);
    const data = tableData.map(item => item[dataKey]);

    const commonDatasetProperties = {
      label: chartType === 'bubble' || chartType === 'scatter' ? t('Account Closing Balance') : t('Account Balance'),
      data: chartType === 'bubble' || chartType === 'scatter' ? data.map((balance, index) => ({ x: index + 1, y: balance })) : data,
      backgroundColor: chartType === 'doughnut' ? getRandomColors(tableData.length) : 'rgba(54, 162, 235, 0.7)',
    };

    return {
      labels,
      datasets: [commonDatasetProperties],
    };
  };

  const accountNames = cashFlowData.map((item) => item.name);
  const cashSpent = cashFlowData.map((item) => item.cashSpent);
  const cashReceived = cashFlowData.map((item) => item.cashReceived);
  const cashDue = cashFlowData.map((item) => item.cashDue);

  const cashFlowChart = {
    labels: accountNames,
    datasets: [
      {
        label: t('Cash In'),
        data: cashReceived,
        backgroundColor: 'rgba(75, 192, 192, 0.7)',
      },
      {
        label: t('Cash Out'),
        data: cashSpent,
        backgroundColor: 'rgba(255, 99, 132, 0.7)',
      },
      {
        label: t('Cash Remaining'),
        data: cashDue,
        backgroundColor: 'rgba(255, 205, 86, 0.7)',
      },
    ],
  };

  const balanceChartData = {
    labels: chartLabels,
    datasets: [
      {
        label: balanceSheet.ReportTitles[1],
        data: chartDataBalance,
        backgroundColor: ['rgba(75, 192, 192, 0.7)', 'rgba(255, 205, 86, 0.7)', 'rgba(255, 99, 132, 0.7)',],

      },
    ],
  };

  const convertExpensesTable = (tableData, labelKey, dataKey, chartType) => {
    const labels = tableData.map((item) => item[labelKey]);
    const data = tableData.map((item) => item[dataKey]);
    const colors = tableData.map((item) => item.color);

    return {
      labels,
      datasets: [
        {
          label: 'Chart Data',
          data,
          backgroundColor: colors,
          borderColor: 'grey',
          borderWidth: 0.5,
        },
      ],
    };
  };

  useEffect(() => {
    const convertAndSetChartData = (expensesData, setStateFunction, chartLabel) => {
      const expensesChart = convertExpensesTable(expensesData, 'expenseName', 'value', 'doughnut');
      const chartData = {
        labels: expensesChart.labels,
        datasets: [
          {
            label: chartLabel,
            data: expensesChart.datasets[0].data,
            backgroundColor: expensesChart.datasets[0].backgroundColor,
            borderColor: 'grey',
            borderWidth: 0.5,
          },
        ],
      };
      setStateFunction(chartData);
    };

    convertAndSetChartData(expensesDataFive, setExpensesDataFour, 'Expenses Full');
  }, []);

  const ReceivablesData = {
    labels: [t('Open Invoices'), t('Total'), t('Due')],
    datasets: [
      {
        label: t('Receivables'),
        backgroundColor: 'rgba(75,192,192,0.2)',
        borderColor: 'rgba(75,192,192,1)',
        borderWidth: 1,
        hoverBackgroundColor: 'rgba(75,192,192,0.4)',
        hoverBorderColor: 'rgba(75,192,192,1)',
        data: [lengthInvoices, parseFloat(sumAmountPaidInvoices).toFixed(2), parseFloat(sumAmountDueInvoices).toFixed(2)],
      },
    ],
  };

  const PayablesData = {
    labels: [t('Open Bills'), t('Total'), t('Due')],
    datasets: [
      {
        label: t('Payables'),
        backgroundColor: 'rgba(255,99,132,0.2)',
        borderColor: 'rgba(255,99,132,1)',
        borderWidth: 1,
        hoverBackgroundColor: 'rgba(255,99,132,0.4)',
        hoverBorderColor: 'rgba(255,99,132,1)',
        data: [lengthBills, parseFloat(sumAmountPaidBills).toFixed(2), parseFloat(sumAmountDueBills).toFixed(2)],
      },
    ],
  };

  const receivePayChartData = {
    labels: [t('Receivables'), t('Payables')],
    datasets: [
      {
        label: t('Total Amount'),
        backgroundColor: 'rgba(75, 192, 192, 0.7)',
        borderColor: 'rgba(75,192,192,1)',
        hoverBorderColor: 'rgba(75,192,192,1)',
        data: [parseFloat(sumAmountPaidInvoices).toFixed(2), parseFloat(sumAmountPaidBills).toFixed(2)],
        stack: 'Stack 0',
      },
      {
        label: t('Due Amount'),
        backgroundColor: 'rgba(255, 99, 132, 0.7)',
        hoverBorderColor: 'rgba(255,99,132,1)',
        data: [parseFloat(sumAmountDueInvoices).toFixed(2), parseFloat(sumAmountDueBills).toFixed(2)],
        stack: 'Stack 0',
      },
    ],
  };

  const orgName = (data && data.ReportTitles && data.ReportTitles[1]) || "Organization Name";

  const handleLink = (provided) => {
    return () => {
      history.push(provided);
    };
  };

  const bankItems = [
    { label: 'New Transaction', onClick: handleLink("/bank/bank_accounts") },
    { label: 'List of Banks', onClick: handleLink("/bank/bank_accounts") },
    { label: 'Chart of Accounts', onClick: handleLink("/accounts/chart_of_accounts/all") }
  ];

  const receivableItems = [
    { label: 'New Invoice', onClick: handleLink("/accounts_receivable/invoices/edit/new") },
    { label: 'List of Invoices', onClick: handleLink("/accounts_receivable") },
  ];

  const payableItems = [
    { label: 'New Bill', onClick: handleLink("/purchases/bills/edit/new") },
    { label: 'List of Bills', onClick: handleLink("/purchases") },
  ];

  const pnLitems = [
    { label: 'Open Report', onClick: handleLink("/reports/profit_and_loss") },
  ];

  const balanceSheetItems = [
    { label: 'Open Report', onClick: handleLink("/reports/balance_sheet") },
  ];

  const salesAndPurchaseItems = [...receivableItems, ...payableItems]

  return (
    <Page title="Accounting Dashboard" showTitle={false}>

      <div className="text-center" style={{ marginBottom: "30px", fontSize: "2em", marginTop: "10px" }} >
        <p style={{ fontSize: "1.3em" }}><Trans>Welcome to the Accounting Dashboard</Trans></p>
        <p><strong>{orgName}</strong></p>
      </div>

      <div className='my-4' style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>

        {/* Bank Balance Sheet */}
          {bankBalanceChartData.labels && bankBalanceChartData.labels.length > 0 && (
            <div {...cardStyle}>
              <MainCard title={t("Bank Balance")} titleLink="/charts/bankbalance" dropDownItems={bankItems} history={history}>
                <Bar data={bankBalanceChartData} />
              </MainCard>
            </div>
          )}

          {/* Profit and Loss Chart */}
          {profitLossChartData.labels && profitLossChartData.labels.length > 0 && (
            <div {...cardStyle}>
              <MainCard title={t("Profit and Loss")} titleLink="/charts/profit_loss" dropDownItems={pnLitems} history={history}>
                <Line data={profitLossChartData} />
              </MainCard>
            </div>
          )}

          {/* Balance Sheet */}
          {balanceChartData.labels && balanceChartData.labels.length > 0 && (
            <div {...cardStyle}>
              <MainCard title={t("Balance Sheet")} titleLink="/charts/balancechart" dropDownItems={balanceSheetItems} history={history}>
                <Bar data={balanceChartData} />
              </MainCard>
            </div>
          )}

          {/* Expenses Doughnut Chart */}
          {expensesChartDataFour.labels && expensesChartDataFour.labels.length > 1 && (
            <div {...cardStyle}>
              <MainCard title={t("Expenses")} titleLink="/charts/expenses" dropDownItems={pnLitems} history={history}>
                <Doughnut data={expensesChartDataFour} />
              </MainCard>
            </div>
          )}
          
          {/* Cash Flow Chart */}
          {cashFlowChart.labels && cashFlowChart.labels.length > 0 && (
            <div {...cardStyle}>
              <MainCard title={t("Cash Flow")} titleLink="/charts/bankbalance" dropDownItems={bankItems} history={history}>
                <Bar data={cashFlowChart} />
              </MainCard>
            </div>
          )}

          {/* Sales Purchase */}          
          {invoiceData && invoiceData.length > 0 && (
            <div {...cardStyle}>
              <MainCard title={t("Sales and Purchases")} titleLink="/charts/receivable_payable" dropDownItems={salesAndPurchaseItems} history={history}>
                <HorizontalBar data={receivePayChartData} />
              </MainCard>
            </div>
          )}

          {/* Receivables Card */}
          <div {...cardStyle}>
              <MainCard title={t("Receivables")} titleLink="/charts/receivable_payable" dropDownItems={receivableItems} history={history}>
                <BlockCard data={ReceivablesData} colors={cardColors} />
              </MainCard>
          </div>

          {/* Payables Card */}
          <div {...cardStyle}>
              <MainCard title={t("Payables")} titleLink="/charts/receivable_payable" dropDownItems={payableItems} history={history}>
                <BlockCard data={PayablesData} colors={cardColors} />
              </MainCard>
          </div>

          <div {...cardStyle}>

          </div>

        </div>
    </Page>
  );
};

export const enhance = compose(
  withReload(),
  withStateHandlers(
    {},
    {
      setState: () => (state, props) => ({
        ...state,
        ...props,
      }),
    }
  ),
  withPropsOnChange([], ({ xero, match, setState, t }) => {
    const { id } = match.params;
    Promise.props({
      item: xero.get("reports/ProfitAndLoss", {
        params: {
          extra_params: {
            fromDate: getMonthStart(),
            toDate: getMonthEnd(),
            periods: 5,
            compare: "periods",
            timeframe: "MONTH",
          }
        }
      }).get("data"),
      invoiceData: xero.get("invoices", {
        params: {
          extra_params: {
            limit: 100,
            where: 'Status="AUTHORISED"'
          }
        }
      }).get("data"),
      balanceSheet: xero.get("reports/BalanceSheet", {
        params: {
          extra_params: {
            date: formatDate(moment().endOf("month")),
            periods: 1,
            timeframe: "YEAR",
          }
        },
      }).get("data"),
      bankdata: xero.get("reports/BankSummary", {
        params: {
          extra_params: {
            fromDate: formatDate(moment().startOf("YEAR")),
            toDate: formatDate(moment().endOf("YEAR")),
          }
        }
      }).get("data"),
      ready: true,
    }).then(setState);
  }),
  branch(
    (props) => !props.ready,
    () => () => <Page><LoadingMsg /></Page> 
  ),
  withXeroErrors(),
  withHandlers({
    handleAddAnotherPerson:
      ({ dispatch }) =>
        () => {
          dispatch(actions.push("local.ContactPersons", {}));
        },
    handleCopyPostal:
      ({ dispatch }) =>
        () => {
          dispatch(actions.change("local."));
        },
    handleRemovePerson:
      ({ dispatch }) =>
        (idx) => {
          dispatch(actions.remove("local.ContactPersons", idx));
        },
  }),
);

export default enhance(Dash);
